import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileText, Plus, Download, Send, CheckCircle2, XCircle, MessageSquare, Clock, X, Trash2, Upload, ImagePlus, SlidersHorizontal, Eraser, Pencil, History } from "lucide-react";

const STATUS_META = {
  pending_approval: { label: "Pendiente", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  approved: { label: "Aprobada", color: "bg-green-100 text-green-800 border-green-200" },
  rejected: { label: "Rechazada", color: "bg-red-100 text-red-700 border-red-200" },
};

export default function QuotationsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [courses, setCourses] = useState([]);
  const [config, setConfig] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showFeedback, setShowFeedback] = useState(null);
  const [showReject, setShowReject] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterCreatedBy, setFilterCreatedBy] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (filter) params.set("status", filter);
    if (isAdmin) {
      if (filterClient) params.set("client", filterClient);
      if (filterCreatedBy) params.set("created_by", filterCreatedBy);
      if (filterDateFrom) params.set("date_from", filterDateFrom);
      if (filterDateTo) params.set("date_to", filterDateTo);
    }
    const qs = params.toString();
    const [q, c, cfg, sig] = await Promise.all([
      api.get(qs ? `/quotations?${qs}` : "/quotations"),
      api.get("/courses"),
      api.get("/quotations-config"),
      api.get("/users/me/signature").catch(() => ({ data: { has_signature: false } })),
    ]);
    setList(q.data);
    setCourses(c.data);
    setConfig(cfg.data);
    setHasSignature(sig.data.has_signature);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, filterClient, filterCreatedBy, filterDateFrom, filterDateTo]);

  const clearFilters = () => {
    setFilterClient(""); setFilterCreatedBy(""); setFilterDateFrom(""); setFilterDateTo("");
  };
  const activeFiltersCount = [filterClient, filterCreatedBy, filterDateFrom, filterDateTo].filter(Boolean).length;

  const downloadPdf = async (id, folio) => {
    try {
      const r = await api.get(`/quotations/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cotizacion-${folio}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const sendToClient = async (id) => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await api.post(`/quotations/${id}/send-to-client`);
      setMsg(`Cotización enviada a ${r.data.sent_to}`);
      load();
      setTimeout(() => setMsg(""), 3500);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const approve = async (id) => {
    setBusy(true); setErr("");
    try { await api.post(`/quotations/${id}/approve`); load(); setDetail(null); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!showReject) return;
    setBusy(true); setErr("");
    try { await api.post(`/quotations/${showReject.id}/reject`, { reason: showReject.reason }); load(); setShowReject(null); setDetail(null); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const sendFeedback = async () => {
    if (!showFeedback?.comment) return;
    setBusy(true); setErr("");
    try { await api.post(`/quotations/${showFeedback.id}/feedback`, { comment: showFeedback.comment }); load(); setShowFeedback(null); setDetail(null); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <FileText className="w-6 h-6 text-naranja" />
          <h2 className="font-heading font-bold text-marino text-xl">Cotizaciones</h2>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="filter-status" value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 border border-gris rounded-lg text-sm bg-white">
            <option value="">Todos los estados</option>
            <option value="pending_approval">Pendientes</option>
            <option value="approved">Aprobadas</option>
            <option value="rejected">Rechazadas</option>
          </select>
          {isAdmin && (
            <button
              data-testid="btn-toggle-filters"
              onClick={() => setShowFilters((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${showFilters || activeFiltersCount ? "bg-naranja/10 text-naranja border-naranja/40" : "bg-marino/5 text-marino border-gris"}`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filtros
              {activeFiltersCount > 0 && (
                <span data-testid="active-filters-count" className="bg-naranja text-white text-[10px] font-bold rounded-full w-5 h-5 inline-flex items-center justify-center">{activeFiltersCount}</span>
              )}
            </button>
          )}
          <button
            data-testid="btn-toggle-signature"
            onClick={() => setShowSignature(true)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${hasSignature ? "bg-green-50 text-green-700 border-green-200" : "bg-marino/5 text-marino border-gris"}`}
          >
            <ImagePlus className="w-4 h-4" /> {hasSignature ? "Firma cargada" : "Cargar firma"}
          </button>
          <button data-testid="btn-new-quotation" onClick={() => setShowForm(true)} className="bg-naranja text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-naranjaHover inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Nueva cotización
          </button>
        </div>
      </div>

      {/* Filter panel (admin only) */}
      {isAdmin && showFilters && (
        <div data-testid="filter-panel" className="bg-white border border-gris/30 rounded-xl p-4 shadow-sm animate-fade-in">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-marino/60 font-bold mb-1">Cliente</label>
              <input
                data-testid="filter-client"
                value={filterClient}
                onChange={(e) => setFilterClient(e.target.value)}
                placeholder="Razón social..."
                className="w-full px-3 py-2 border border-gris rounded-lg text-sm focus:border-marino focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-marino/60 font-bold mb-1">Elaborado por</label>
              <input
                data-testid="filter-created-by"
                value={filterCreatedBy}
                onChange={(e) => setFilterCreatedBy(e.target.value)}
                placeholder="Nombre del emisor..."
                className="w-full px-3 py-2 border border-gris rounded-lg text-sm focus:border-marino focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-marino/60 font-bold mb-1">Desde</label>
              <input
                data-testid="filter-date-from"
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gris rounded-lg text-sm focus:border-marino focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-marino/60 font-bold mb-1">Hasta</label>
              <input
                data-testid="filter-date-to"
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gris rounded-lg text-sm focus:border-marino focus:outline-none"
              />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-marino/60">{list.length} resultado{list.length === 1 ? "" : "s"}</span>
            {activeFiltersCount > 0 && (
              <button
                data-testid="btn-clear-filters"
                onClick={clearFilters}
                className="inline-flex items-center gap-1.5 text-xs text-naranja hover:text-naranjaHover font-semibold"
              >
                <Eraser className="w-3.5 h-3.5" /> Limpiar filtros
              </button>
            )}
          </div>
        </div>
      )}

      {err && <div data-testid="err-banner" className="text-naranja text-sm bg-naranja/10 border border-naranja/30 rounded-lg p-3">{err}</div>}
      {msg && <div data-testid="msg-banner" className="text-green-800 text-sm bg-green-50 border border-green-200 rounded-lg p-3 inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {msg}</div>}

      {/* List */}
      <div className="bg-white border border-gris/30 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-marino/5 text-marino/80 text-xs uppercase tracking-wider">
              <tr>
                <th className="p-3 text-left">Folio</th>
                <th className="p-3 text-left">Cliente</th>
                <th className="p-3 text-left">Elaborado por</th>
                <th className="p-3 text-right">Subtotal</th>
                <th className="p-3 text-center">Estado</th>
                <th className="p-3 text-center">Fecha</th>
                <th className="p-3 text-center w-56">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {list.map((q) => {
                const meta = STATUS_META[q.status] || { label: q.status, color: "bg-gris/20 text-marino" };
                const isMine = q.created_by_id === user?.id;
                const canDownload = isAdmin || (q.status === "approved" && isMine);
                const canSend = q.status === "approved" && (isAdmin || isMine);
                return (
                  <tr key={q.id} data-testid={`quotation-row-${q.id}`} className="border-b border-gris/10 hover:bg-marino/[0.02]">
                    <td className="p-3 font-mono text-xs text-marino">{q.folio}</td>
                    <td className="p-3">
                      <div className="text-marino font-medium truncate max-w-[180px]">{q.client?.razon_social}</div>
                      <div className="text-xs text-marino/60 truncate max-w-[180px]">{q.client?.email || "sin email"}</div>
                    </td>
                    <td className="p-3 text-marino/80">{q.created_by_name}</td>
                    <td className="p-3 text-right font-semibold text-marino">${(q.subtotal_amount || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td>
                    <td className="p-3 text-center">
                      <span data-testid={`status-${q.id}`} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full font-semibold border ${meta.color}`}>{meta.label}</span>
                    </td>
                    <td className="p-3 text-center text-xs text-marino/70">{(q.created_at || "").slice(0, 10)}</td>
                    <td className="p-3">
                      <div className="flex justify-center gap-1 flex-wrap">
                        <button data-testid={`view-${q.id}`} onClick={() => setDetail(q)} title="Ver detalle" className="p-1.5 bg-marino/5 text-marino hover:bg-marino hover:text-white rounded-lg transition-colors"><FileText className="w-4 h-4" /></button>
                        {canDownload && (
                          <button data-testid={`download-${q.id}`} onClick={() => downloadPdf(q.id, q.folio)} title="Descargar PDF" className="p-1.5 bg-naranja/10 text-naranja hover:bg-naranja hover:text-white rounded-lg transition-colors"><Download className="w-4 h-4" /></button>
                        )}
                        {!isAdmin && isMine && q.status === "rejected" && (
                          <button
                            data-testid={`edit-${q.id}`}
                            onClick={() => { setEditing(q); setShowForm(true); }}
                            title="Editar y re-enviar"
                            className="p-1.5 bg-marino text-white hover:bg-marino/90 rounded-lg transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {canSend && q.client?.email && (
                          <button data-testid={`send-${q.id}`} onClick={() => sendToClient(q.id)} title="Enviar al cliente" className="p-1.5 bg-green-100 text-green-700 hover:bg-green-600 hover:text-white rounded-lg transition-colors"><Send className="w-4 h-4" /></button>
                        )}
                        {isAdmin && q.status === "pending_approval" && (
                          <>
                            <button data-testid={`approve-${q.id}`} onClick={() => approve(q.id)} disabled={busy} title="Aprobar" className="p-1.5 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors"><CheckCircle2 className="w-4 h-4" /></button>
                            <button data-testid={`reject-${q.id}`} onClick={() => setShowReject({ id: q.id, reason: "" })} title="Rechazar" className="p-1.5 bg-red-500 text-white hover:bg-red-600 rounded-lg transition-colors"><XCircle className="w-4 h-4" /></button>
                            <button data-testid={`feedback-${q.id}`} onClick={() => setShowFeedback({ id: q.id, comment: "" })} title="Comentarios" className="p-1.5 bg-marino text-white hover:bg-marino/90 rounded-lg transition-colors"><MessageSquare className="w-4 h-4" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-marino/60">Sin cotizaciones</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create form modal */}
      {showForm && config && (
        <QuotationForm
          courses={courses}
          config={config}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => {
            setShowForm(false);
            setEditing(null);
            load();
            const successMsg = editing
              ? "Cotización actualizada y re-enviada para aprobación"
              : (isAdmin ? "Cotización creada y aprobada" : "Cotización enviada a revisión del admin");
            setMsg(successMsg);
            setTimeout(() => setMsg(""), 3500);
          }}
        />
      )}

      {/* Detail modal */}
      {detail && (
        <QuotationDetail q={detail} config={config} onClose={() => setDetail(null)} />
      )}

      {/* Reject modal */}
      {showReject && (
        <Modal title="Rechazar cotización" onClose={() => setShowReject(null)}>
          <p className="text-marino/80 text-sm">Indica el motivo del rechazo para notificar al instructor:</p>
          <textarea
            data-testid="reject-reason"
            value={showReject.reason}
            onChange={(e) => setShowReject({ ...showReject, reason: e.target.value })}
            rows={4}
            className="mt-3 w-full px-3 py-2 border border-gris rounded-lg text-sm resize-none focus:border-marino focus:outline-none"
            placeholder="Ej: Los datos fiscales no coinciden con la razón social."
          />
          <div className="flex gap-2 mt-4">
            <button onClick={() => setShowReject(null)} className="flex-1 bg-gris text-marino px-4 py-2 rounded-lg text-sm font-semibold hover:bg-grisHover">Cancelar</button>
            <button data-testid="submit-reject" onClick={reject} disabled={busy || !showReject.reason} className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-600 disabled:opacity-50">Rechazar</button>
          </div>
        </Modal>
      )}

      {/* Feedback modal */}
      {showFeedback && (
        <Modal title="Comentarios de modificación" onClose={() => setShowFeedback(null)}>
          <p className="text-marino/80 text-sm">Envía retroalimentación al instructor para que ajuste la cotización:</p>
          <textarea
            data-testid="feedback-comment"
            value={showFeedback.comment}
            onChange={(e) => setShowFeedback({ ...showFeedback, comment: e.target.value })}
            rows={5}
            className="mt-3 w-full px-3 py-2 border border-gris rounded-lg text-sm resize-none focus:border-marino focus:outline-none"
            placeholder="Ej: Ajusta la cantidad del curso de Excel a 8 personas y valida la dirección fiscal."
          />
          <div className="flex gap-2 mt-4">
            <button onClick={() => setShowFeedback(null)} className="flex-1 bg-gris text-marino px-4 py-2 rounded-lg text-sm font-semibold hover:bg-grisHover">Cancelar</button>
            <button data-testid="submit-feedback" onClick={sendFeedback} disabled={busy || !showFeedback.comment} className="flex-1 bg-marino text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-marino/90 disabled:opacity-50">Enviar</button>
          </div>
        </Modal>
      )}

      {/* Signature upload */}
      {showSignature && (
        <SignatureModal
          hasSignature={hasSignature}
          onClose={() => setShowSignature(false)}
          onSaved={() => { setHasSignature(true); setShowSignature(false); setMsg("Firma cargada"); setTimeout(() => setMsg(""), 2500); }}
          onDeleted={() => { setHasSignature(false); setShowSignature(false); }}
        />
      )}
    </div>
  );
}

// -------- Modal wrapper --------
function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] bg-marino/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in">
        <div className="bg-marino text-white px-6 py-4 flex items-center justify-between">
          <h3 className="font-heading font-bold">{title}</h3>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// -------- Create/Edit form --------
function QuotationForm({ courses, config, editing, onClose, onSaved }) {
  const isEditing = !!editing;
  const [client, setClient] = useState(editing?.client
    ? { razon_social: editing.client.razon_social || "", rfc: editing.client.rfc || "", direccion: editing.client.direccion || "", atencion: editing.client.atencion || "", email: editing.client.email || "" }
    : { razon_social: "", rfc: "", direccion: "", atencion: "", email: "" });
  const [uso, setUso] = useState(editing?.additional_info?.uso_cfdi || "G03");
  const [forma, setForma] = useState(editing?.additional_info?.forma_pago || "Transferencia");
  const [items, setItems] = useState(editing?.items?.length
    ? editing.items.map((it) => ({ course_id: it.course_id, quantity: it.quantity, discount_percent: it.discount_percent || 0 }))
    : [{ course_id: "", quantity: 1, discount_percent: 0 }]);
  const [globalDiscount, setGlobalDiscount] = useState(editing?.global_discount_percent || 0);
  const [instructorNote, setInstructorNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const totals = useMemo(() => {
    let gross = 0;
    let lineDisc = 0;
    for (const it of items) {
      const c = courses.find((c) => c.id === it.course_id);
      if (!c) continue;
      const g = Number(c.price || 0) * Number(it.quantity || 0);
      const dp = Math.min(100, Math.max(0, Number(it.discount_percent || 0)));
      gross += g;
      lineDisc += g * dp / 100;
    }
    const afterLines = gross - lineDisc;
    const gd = Math.min(100, Math.max(0, Number(globalDiscount || 0)));
    const globalDiscAmt = afterLines * gd / 100;
    const final = afterLines - globalDiscAmt;
    return { gross, lineDisc, afterLines, globalDiscAmt, final };
  }, [items, courses, globalDiscount]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        client,
        items: items.filter((i) => i.course_id).map((i) => ({
          course_id: i.course_id,
          quantity: parseInt(i.quantity),
          discount_percent: Number(i.discount_percent || 0),
        })),
        uso_cfdi: uso,
        forma_pago: forma,
        global_discount_percent: Number(globalDiscount || 0),
      };
      if (payload.items.length === 0) throw new Error("Agrega al menos un curso");
      if (isEditing) {
        payload.instructor_note = instructorNote || null;
        await api.put(`/quotations/${editing.id}`, payload);
      } else {
        await api.post("/quotations", payload);
      }
      onSaved();
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-marino/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden animate-fade-in my-8">
        <div className="bg-marino text-white px-6 py-4 flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">{isEditing ? `Editar ${editing.folio}` : "Nueva cotización"}</h3>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {isEditing && editing?.rejection_reason && (
            <div data-testid="rejection-banner" className="bg-red-50 border-l-4 border-red-500 border border-red-200 rounded-lg p-3">
              <div className="text-red-700 text-xs uppercase tracking-wider font-bold mb-1">Motivo del rechazo previo</div>
              <div className="text-marino text-sm">{editing.rejection_reason}</div>
              {editing.reviewed_by_name && <div className="text-marino/50 text-[11px] mt-1">— {editing.reviewed_by_name}</div>}
            </div>
          )}
          {/* Cotizado a */}
          <section>
            <h4 className="text-marino font-bold text-sm uppercase tracking-wider mb-3 border-b border-naranja pb-1 inline-block">Cotizado a</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="client-razon" required placeholder="Razón Social / Nombre" value={client.razon_social} onChange={(e) => setClient({ ...client, razon_social: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
              <input data-testid="client-rfc" required placeholder="RFC (12 o 13 caracteres)" value={client.rfc} onChange={(e) => setClient({ ...client, rfc: e.target.value.toUpperCase() })} className="px-3 py-2 border border-gris rounded-lg text-sm uppercase" />
              <input data-testid="client-direccion" required placeholder="Dirección (Calle, No, Col, CP, Ciudad, Estado)" value={client.direccion} onChange={(e) => setClient({ ...client, direccion: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm sm:col-span-2" />
              <input data-testid="client-atencion" required placeholder="Atención (nombre del contacto)" value={client.atencion} onChange={(e) => setClient({ ...client, atencion: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
              <input data-testid="client-email" type="email" placeholder="Email del cliente (para envío directo)" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
            </div>
          </section>

          {/* Info Adicional */}
          <section>
            <h4 className="text-marino font-bold text-sm uppercase tracking-wider mb-3 border-b border-naranja pb-1 inline-block">Información Adicional</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <select data-testid="uso-cfdi" value={uso} onChange={(e) => setUso(e.target.value)} className="px-3 py-2 border border-gris rounded-lg text-sm bg-white">
                {Object.entries(config.uso_cfdi_options).map(([k, v]) => <option key={k} value={k}>{k} · {v}</option>)}
              </select>
              <select data-testid="forma-pago" value={forma} onChange={(e) => setForma(e.target.value)} className="px-3 py-2 border border-gris rounded-lg text-sm bg-white">
                {config.forma_pago_options.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </section>

          {/* Items */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-marino font-bold text-sm uppercase tracking-wider border-b border-naranja pb-1 inline-block">Cursos</h4>
              <button type="button" data-testid="add-item" onClick={() => setItems([...items, { course_id: "", quantity: 1, discount_percent: 0 }])} className="text-naranja text-xs font-semibold inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Agregar curso</button>
            </div>
            <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-marino/50 font-bold mb-1 px-1">
              <div className="col-span-6">Curso</div>
              <div className="col-span-2 text-center">Cant.</div>
              <div className="col-span-2 text-center">Desc. %</div>
              <div className="col-span-1 text-right">Total</div>
              <div className="col-span-1" />
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => {
                const c = courses.find((c) => c.id === it.course_id);
                const gross = c ? Number(c.price) * Number(it.quantity || 0) : 0;
                const dp = Math.min(100, Math.max(0, Number(it.discount_percent || 0)));
                const net = gross - gross * dp / 100;
                return (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <select data-testid={`item-course-${idx}`} value={it.course_id} onChange={(e) => { const n = [...items]; n[idx].course_id = e.target.value; setItems(n); }} className="col-span-6 px-3 py-2 border border-gris rounded-lg text-sm bg-white">
                      <option value="">Selecciona un curso...</option>
                      {courses.map((cc) => <option key={cc.id} value={cc.id}>{cc.title} · ${Number(cc.price).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</option>)}
                    </select>
                    <input data-testid={`item-qty-${idx}`} type="number" min="1" value={it.quantity} onChange={(e) => { const n = [...items]; n[idx].quantity = e.target.value; setItems(n); }} className="col-span-2 px-3 py-2 border border-gris rounded-lg text-sm text-center" />
                    <div className="col-span-2 relative">
                      <input
                        data-testid={`item-discount-${idx}`}
                        type="number" min="0" max="100" step="0.5"
                        value={it.discount_percent}
                        onChange={(e) => { const n = [...items]; n[idx].discount_percent = e.target.value; setItems(n); }}
                        className="w-full pl-3 pr-6 py-2 border border-gris rounded-lg text-sm text-right"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-marino/50 text-xs">%</span>
                    </div>
                    <div className="col-span-1 text-right text-xs text-marino font-semibold">${net.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</div>
                    <button type="button" data-testid={`remove-item-${idx}`} onClick={() => setItems(items.filter((_, i) => i !== idx))} disabled={items.length === 1} className="col-span-1 text-red-500 hover:text-red-700 disabled:opacity-30 justify-self-end"><Trash2 className="w-4 h-4" /></button>
                  </div>
                );
              })}
            </div>

            {/* Global discount + Totals */}
            <div className="mt-4 pt-3 border-t border-gris/30 space-y-1.5">
              <div className="flex justify-between items-center text-sm">
                <span className="text-marino/70">Subtotal bruto:</span>
                <span data-testid="form-gross-subtotal" className="text-marino font-semibold">${totals.gross.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
              </div>
              {totals.lineDisc > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-marino/70">Descuento por línea:</span>
                  <span className="text-naranja font-semibold">-${totals.lineDisc.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm gap-3">
                <label className="text-marino/70 inline-flex items-center gap-2">
                  Descuento global (%):
                  <input
                    data-testid="global-discount"
                    type="number" min="0" max="100" step="0.5"
                    value={globalDiscount}
                    onChange={(e) => setGlobalDiscount(e.target.value)}
                    className="w-20 px-2 py-1 border border-gris rounded-lg text-sm text-right"
                  />
                </label>
                {totals.globalDiscAmt > 0 && (
                  <span className="text-naranja font-semibold">-${totals.globalDiscAmt.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gris/30">
                <span className="text-marino uppercase tracking-wider text-sm font-bold">Total final:</span>
                <span data-testid="form-final-total" className="text-naranja text-2xl font-black">${totals.final.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </section>

          {err && <div className="text-naranja text-sm bg-naranja/10 border border-naranja/30 rounded-lg p-3">{err}</div>}

          {isEditing && (
            <section>
              <h4 className="text-marino font-bold text-sm uppercase tracking-wider mb-2 border-b border-naranja pb-1 inline-block">Comentario para el revisor (opcional)</h4>
              <textarea
                data-testid="instructor-note"
                value={instructorNote}
                onChange={(e) => setInstructorNote(e.target.value)}
                rows={3}
                placeholder="Ej: Ajusté las cantidades y agregué el descuento solicitado. Favor de revisar nuevamente."
                className="w-full px-3 py-2 border border-gris rounded-lg text-sm resize-none focus:border-marino focus:outline-none"
              />
              <p className="text-marino/50 text-xs mt-1">Este mensaje se agregará al historial y llegará al equipo de administración junto con la cotización re-enviada.</p>
            </section>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-gris text-marino px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-grisHover">Cancelar</button>
            <button data-testid="submit-quotation" disabled={busy} className="flex-1 bg-naranja text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-naranjaHover disabled:opacity-60">{busy ? "Guardando..." : (isEditing ? "Re-enviar para aprobación" : "Generar cotización")}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

// -------- Detail modal --------
function QuotationDetail({ q, config, onClose }) {
  const meta = STATUS_META[q.status] || { label: q.status, color: "bg-gris/20 text-marino" };
  const usoDesc = config?.uso_cfdi_options?.[q.additional_info?.uso_cfdi] || q.additional_info?.uso_cfdi;
  return (
    <div className="fixed inset-0 z-[80] bg-marino/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-fade-in my-8">
        <div className="bg-marino text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-lg">{q.folio}</h3>
            <div className="text-xs text-white/60 mt-0.5">Elaborado por {q.created_by_name} · {(q.created_at || "").slice(0, 10)}</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5 text-sm">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full font-semibold border ${meta.color}`}>{meta.label}</span>
            {q.reviewed_by_name && <span className="text-marino/60 text-xs">Revisado por {q.reviewed_by_name}</span>}
          </div>
          <section>
            <h4 className="text-marino/60 uppercase tracking-wider text-xs font-bold mb-2">Cliente</h4>
            <div className="bg-marino/5 rounded-lg p-3 space-y-1">
              <div className="font-semibold text-marino">{q.client?.razon_social}</div>
              <div className="text-marino/70 text-xs">RFC: {q.client?.rfc} · {q.client?.email || "sin email"}</div>
              <div className="text-marino/70 text-xs">{q.client?.direccion}</div>
              <div className="text-marino/70 text-xs">Atención: {q.client?.atencion}</div>
            </div>
          </section>
          <section>
            <h4 className="text-marino/60 uppercase tracking-wider text-xs font-bold mb-2">Cursos</h4>
            <table className="w-full text-xs">
              <thead className="bg-marino/5 text-marino/70">
                <tr><th className="text-left p-2">Curso</th><th className="text-center p-2">Cant.</th><th className="text-right p-2">Unit.</th><th className="text-center p-2">Desc.</th><th className="text-right p-2">Total</th></tr>
              </thead>
              <tbody>
                {(q.items || []).map((it, i) => (
                  <tr key={i} className="border-b border-gris/10">
                    <td className="p-2 text-marino">{it.description}</td>
                    <td className="p-2 text-center">{it.quantity}</td>
                    <td className="p-2 text-right">${Number(it.unit_cost).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td>
                    <td className="p-2 text-center">{it.discount_percent > 0 ? <span className="text-naranja font-semibold">{Number(it.discount_percent).toFixed(1)}%</span> : <span className="text-marino/30">—</span>}</td>
                    <td className="p-2 text-right font-semibold">${Number(it.subtotal).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td colSpan={4} className="p-2 text-right text-marino/60">Subtotal bruto:</td><td className="p-2 text-right font-semibold">${Number(q.gross_subtotal || q.subtotal_amount).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td></tr>
                {q.line_discount_total > 0 && (
                  <tr><td colSpan={4} className="p-2 text-right text-marino/60">Descuento por línea:</td><td className="p-2 text-right text-naranja font-semibold">-${Number(q.line_discount_total).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td></tr>
                )}
                {q.global_discount_amount > 0 && (
                  <tr><td colSpan={4} className="p-2 text-right text-marino/60">Descuento global ({Number(q.global_discount_percent).toFixed(1)}%):</td><td className="p-2 text-right text-naranja font-semibold">-${Number(q.global_discount_amount).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td></tr>
                )}
                <tr><td colSpan={4} className="p-2 text-right text-marino uppercase tracking-wider font-bold border-t border-naranja">Total:</td><td className="p-2 text-right text-naranja font-black text-base border-t border-naranja">${Number(q.subtotal_amount).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td></tr>
              </tfoot>
            </table>
            <div className="text-marino/60 text-xs mt-2 italic">{q.amount_in_words}</div>
          </section>
          <section className="grid grid-cols-2 gap-3">
            <div><span className="text-marino/60 uppercase tracking-wider text-xs font-bold">Uso CFDI</span><div className="text-marino">{q.additional_info?.uso_cfdi} · {usoDesc}</div></div>
            <div><span className="text-marino/60 uppercase tracking-wider text-xs font-bold">Forma de pago</span><div className="text-marino">{q.additional_info?.forma_pago}</div></div>
          </section>
          {q.status === "rejected" && q.rejection_reason && (
            <div className="bg-red-50 border-l-4 border-red-500 border border-red-200 rounded-lg p-3">
              <div className="text-red-700 text-xs uppercase tracking-wider font-bold mb-1">Motivo del rechazo</div>
              <div className="text-marino text-sm">{q.rejection_reason}</div>
            </div>
          )}
          {q.feedback_comment && (
            <div className="bg-naranja/5 border-l-4 border-naranja rounded-lg p-3">
              <div className="text-naranja text-xs uppercase tracking-wider font-bold mb-1">Último comentario del revisor</div>
              <div className="text-marino text-sm whitespace-pre-wrap">{q.feedback_comment}</div>
              <div className="text-marino/60 text-xs mt-1">— {q.feedback_by_name}</div>
            </div>
          )}

          {q.feedback_history && q.feedback_history.length > 0 && (
            <section data-testid="feedback-history">
              <h4 className="text-marino/60 uppercase tracking-wider text-xs font-bold mb-2 inline-flex items-center gap-1.5">
                <History className="w-3.5 h-3.5" /> Historial de comentarios ({q.feedback_history.length})
              </h4>
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                {[...q.feedback_history].reverse().map((h, i) => {
                  const meta = {
                    rejection: { label: "Rechazo", color: "bg-red-50 border-red-200 text-red-700" },
                    admin_feedback: { label: "Comentario admin", color: "bg-naranja/5 border-naranja/30 text-naranja" },
                    instructor_resubmit: { label: "Re-envío instructor", color: "bg-marino/5 border-marino/20 text-marino" },
                    admin_edit: { label: "Edición admin", color: "bg-marino/5 border-marino/20 text-marino" },
                  }[h.type] || { label: h.type, color: "bg-gris/10 border-gris/30 text-marino" };
                  return (
                    <div key={i} data-testid={`history-entry-${i}`} className={`border rounded-lg p-2.5 ${meta.color}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-wider font-bold">{meta.label}</span>
                        <span className="text-[10px] opacity-70">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                      <div className="text-marino text-xs whitespace-pre-wrap">{h.message || "(sin mensaje)"}</div>
                      <div className="text-marino/50 text-[10px] mt-1">— {h.by}</div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
          {q.sent_to_client_at && (
            <div className="text-green-700 text-xs flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Enviada al cliente el {q.sent_to_client_at.slice(0, 10)} por {q.sent_to_client_by}</div>
          )}
          {q.onedrive_backup_url && (
            <a data-testid="onedrive-backup-link" href={q.onedrive_backup_url} target="_blank" rel="noopener noreferrer" className="text-marino text-xs flex items-center gap-1 hover:text-naranja transition-colors">
              <CheckCircle2 className="w-4 h-4 text-green-600" /> Respaldo OneDrive · <span className="underline">Abrir en Cotizaciones Aprobadas</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// -------- Signature modal --------
function SignatureModal({ hasSignature, onClose, onSaved, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onSaved();
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true); setErr("");
    try { await api.delete("/users/me/signature"); onDeleted(); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Firma digital" onClose={onClose}>
      <p className="text-marino/80 text-sm mb-3">Sube una imagen PNG o JPG con fondo transparente. Se usará en el pie de las cotizaciones donde aparezcas como "Elaborado por" o "Revisado por".</p>
      <label className="w-full inline-flex items-center justify-center gap-2 bg-naranja text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-naranjaHover cursor-pointer">
        <Upload className="w-4 h-4" /> {hasSignature ? "Reemplazar firma" : "Subir firma"}
        <input data-testid="signature-input" type="file" accept="image/png,image/jpeg" className="hidden" onChange={upload} disabled={busy} />
      </label>
      {hasSignature && (
        <button data-testid="delete-signature" onClick={remove} disabled={busy} className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-red-50 text-red-700 border border-red-200 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-100">
          <Trash2 className="w-4 h-4" /> Eliminar firma actual
        </button>
      )}
      {err && <div className="text-naranja text-sm mt-2">{err}</div>}
      <p className="text-marino/50 text-xs mt-3">Si no cargas una imagen, el sistema imprimirá tu nombre en tipografía tipo firma como respaldo.</p>
    </Modal>
  );
}
